import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, InputGroup, Modal, Row } from "react-bootstrap";
import { CallBack, Claims } from "../../models";
import { useCallBackQuery } from "../../queries";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions, useProfile, useToasts } from "../../contexts";
import { createPatch } from "rfc6902";
import { useCurrentCase } from "../../hooks";
import { displayDateAndTime } from "../../helper-functions";

interface CallBackModalProps {
    onHide: () => void;
    show: boolean;
    callBack?: CallBack;
}

export function CallBackModal({ onHide, show, callBack }: Readonly<CallBackModalProps>) {
    const { referralId, listSlug } = useParams<{ referralId: string; listSlug: string }>();
    const { caseId } = useCurrentCase();
    const { errorToast } = useToasts();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const { currentUser } = useProfile();
    const { createMutation, updateMutation } = useCallBackQuery({ id: callBack?.id });
    const [name, setName] = useState<string>("");
    const [note, setNote] = useState<string>("");
    const [hours, setHours] = useState<number>(0);
    const [minutes, setMinutes] = useState<number>(0);
    const [scheduledTimeInput, setScheduledTimeInput] = useState<string>("");
    const [isPendingFollowUp, setIsPendingFollowUp] = useState<boolean>(false);
    const [completedOn, setCompletedOn] = useState<string>("");
    const isOnDashboard = !referralId;
    const maxNameLength = 200;
    const maxNoteLength = 500;

    const clearInputs = () => {
        setName("");
        setNote("");
        setHours(0);
        setMinutes(0);
        setScheduledTimeInput("");
        setIsPendingFollowUp(false);
        setCompletedOn("");
    };

    const loadCallBack = useCallback(() => {
        if (callBack) {
            setName(callBack.name);
            setNote(callBack.note);
            setScheduledTimeInput(callBack.scheduledTime);
            setIsPendingFollowUp(callBack.isPendingFollowUp);
            setCompletedOn(callBack.completedOn ? new Date(callBack.completedOn).toISOString() : "");

            if (callBack.scheduledTime) {
                const now = new Date();
                const scheduledDateTime = new Date(callBack.scheduledTime);
                if (scheduledDateTime.getTime() < now.getTime()) return;
                const diffInHours = Math.abs(now.getTime() - scheduledDateTime.getTime()) / (1000 * 60 * 60);

                setHours(Math.floor(diffInHours));
                setMinutes(Math.floor((diffInHours % 1) * 60));
            }
        } else {
            clearInputs();
        }
    }, [callBack]);

    useEffect(() => {
        loadCallBack();
    }, [loadCallBack]);

    const calculatedScheduledTime = useMemo(() => {
        const now = new Date();
        now.setHours(now.getHours() + hours);
        now.setMinutes(now.getMinutes() + minutes);
        return now.toISOString();
    }, [hours, minutes]);

    const getDisplayedScheduledTime = useCallback(() => {
        if (scheduledTimeInput) return `Scheduled time: ${displayDateAndTime(scheduledTimeInput)}`;

        if (!hours && !minutes) return <span className="text-danger">Enter hours and/or minutes.</span>;

        return `Scheduled time: ${displayDateAndTime(calculatedScheduledTime)}`;
    }, [calculatedScheduledTime, hours, minutes, scheduledTimeInput]);

    const createCallback = useCallback(() => {
        if (!currentUser || !hasPermission(Claims.CreateCallBack)) return;
        const newCallBack: Partial<CallBack> = {
            name,
            note,
            scheduledTime: calculatedScheduledTime,
            isPendingFollowUp,
            completedOn,
            caseId: Number(caseId),
            userId: currentUser.id,
            userName: `${currentUser.firstName} ${currentUser.lastName}`,
        };
        createMutation.mutate(newCallBack);
    }, [
        calculatedScheduledTime,
        caseId,
        completedOn,
        createMutation,
        currentUser,
        hasPermission,
        isPendingFollowUp,
        name,
        note,
    ]);

    const editCallback = useCallback(
        async (markComplete: boolean | undefined = undefined) => {
            if (!callBack || !hasPermission(Claims.EditCallBack)) return;
            const completedOnResult = markComplete
                ? new Date().toISOString()
                : markComplete === false
                  ? ""
                  : completedOn;

            const updatedCallBack: CallBack = {
                ...callBack,
                name,
                note,
                scheduledTime: scheduledTimeInput || calculatedScheduledTime,
                isPendingFollowUp,
                completedOn: completedOnResult,
            };
            const operations = createPatch(callBack, updatedCallBack);
            try {
                await updateMutation.mutateAsync(operations);
            } catch (e: unknown) {
                errorToast(e);
            }
        },
        [
            calculatedScheduledTime,
            callBack,
            completedOn,
            errorToast,
            hasPermission,
            isPendingFollowUp,
            name,
            note,
            scheduledTimeInput,
            updateMutation,
        ]
    );

    const isValid = useMemo(() => {
        if (!name) return false;
        if (!scheduledTimeInput && !hours && !minutes) return false;
        return true;
    }, [name, scheduledTimeInput, hours, minutes]);

    const handleSave = useCallback(
        async (markComplete: boolean | undefined = undefined) => {
            if (!isValid) return;
            if (callBack) {
                await editCallback(markComplete);
            } else {
                createCallback();
            }
            onHide();
            clearInputs();
        },
        [callBack, createCallback, editCallback, isValid, onHide]
    );

    return (
        <Modal onHide={onHide} show={show} centered>
            <Modal.Header closeButton>{callBack ? "Edit" : "New"} Call Back</Modal.Header>
            <fieldset disabled={!!completedOn || (!!callBack && !hasPermission(Claims.EditCallBack))}>
                <Modal.Body>
                    {!!completedOn && (
                        <Alert variant="info">
                            This Call Back is complete. To make changes, first mark it as incomplete.
                        </Alert>
                    )}
                    <Form.Check
                        type="checkbox"
                        id="pendingFollowUp"
                        label="Pending Follow Up"
                        aria-label="Pending Follow Up"
                        checked={isPendingFollowUp}
                        onChange={(e) => setIsPendingFollowUp(e.target.checked)}
                        className="mb-2"
                    />

                    <Form.Group controlId="name" className="mb-2">
                        <Form.Label>From whom are you expecting a call back?</Form.Label>
                        <Form.Control
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            aria-label="From whom are you expecting a call back?"
                            isInvalid={!name}
                            maxLength={maxNameLength}
                        />
                        <Form.Text>Include the person's title if applicable.</Form.Text>
                    </Form.Group>

                    <Form.Group controlId="note" className="mb-2">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            aria-label="Notes"
                            maxLength={maxNoteLength}
                        />
                        <Form.Text>
                            {note.length}/{maxNoteLength} characters
                        </Form.Text>
                    </Form.Group>

                    {!scheduledTimeInput && (
                        <>
                            <Form.Label>How long until you expect to receive a call back?</Form.Label>
                            <Row className="mb-2">
                                <Col>
                                    <InputGroup>
                                        <Form.Control
                                            type="number"
                                            value={hours || ""}
                                            onChange={(e) => setHours(Number(e.target.value))}
                                            aria-label="Hours until expected call back"
                                            isInvalid={!hours && !minutes}
                                            min={0}
                                        />
                                        <InputGroup.Text>Hours</InputGroup.Text>
                                    </InputGroup>
                                </Col>
                                <Col>
                                    <InputGroup>
                                        <Form.Control
                                            type="number"
                                            value={minutes || ""}
                                            onChange={(e) => setMinutes(Number(e.target.value))}
                                            aria-label="Minutes until expected call back"
                                            isInvalid={!hours && !minutes}
                                            min={0}
                                        />
                                        <InputGroup.Text>Minutes</InputGroup.Text>
                                    </InputGroup>
                                </Col>
                            </Row>
                        </>
                    )}
                    <Row>
                        <Col>{getDisplayedScheduledTime()}</Col>
                    </Row>
                    {!!scheduledTimeInput && hasPermission(Claims.EditCallBack) && (
                        <Row>
                            <Button variant="info" size="sm" onClick={() => setScheduledTimeInput("")} className="mt-2">
                                Reschedule
                            </Button>
                        </Row>
                    )}
                    {!!callBack && (
                        <>
                            <hr />
                            Created by {callBack.userName} on {displayDateAndTime(callBack.createdOn)}
                        </>
                    )}
                </Modal.Body>
            </fieldset>
            {(!callBack || hasPermission(Claims.EditCallBack)) && (
                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="secondary" onClick={onHide}>
                        Cancel
                    </Button>
                    {isOnDashboard && !!callBack && (
                        <Button
                            variant="outline-info"
                            onClick={() =>
                                navigate(
                                    `/lists/${listSlug || "none"}/cases/${callBack.caseId}/referral/${callBack.case?.referral?.id}`
                                )
                            }
                        >
                            View Referral
                        </Button>
                    )}
                    <Button variant="primary" onClick={() => handleSave()} disabled={!isValid}>
                        Save
                    </Button>
                    {!!callBack && !completedOn && (
                        <Button variant="info" onClick={() => handleSave(true)} disabled={!isValid}>
                            Mark Complete
                        </Button>
                    )}
                    {!!callBack && !!completedOn && (
                        <Button variant="warning" onClick={() => handleSave(false)} disabled={!isValid}>
                            Mark Incomplete
                        </Button>
                    )}
                </Modal.Footer>
            )}
        </Modal>
    );
}
